package com.bikompanion.ui.home

import androidx.compose.animation.core.spring
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshotFlow
import androidx.compose.runtime.structuralEqualityPolicy
import androidx.lifecycle.compose.LifecycleStartEffect
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import dev.zacsweers.metrox.viewmodel.metroViewModel
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.launch
import org.maplibre.compose.camera.CameraAnimation
import org.maplibre.compose.camera.CameraPosition
import org.maplibre.compose.camera.CameraUpdate
import org.maplibre.compose.location.HeadingRequest
import org.maplibre.compose.location.LocationProvider
import org.maplibre.compose.location.rememberDefaultHeadingProvider
import org.maplibre.compose.location.rememberDefaultLocationProvider
import org.maplibre.compose.location.rememberLocationState
import org.maplibre.compose.map.rememberMapState
import com.bikompanion.ui.composables.UserLocationPuck
import com.bikompanion.ui.composables.buildMapStyle
import com.bikompanion.ui.install.AppInstallOffer
import com.bikompanion.ui.link.StationLinks
import com.bikompanion.ui.models.FoldBounds
import com.bikompanion.ui.models.LocateMode
import com.bikompanion.ui.settings.SettingsPlatform
import com.bikompanion.ui.theme.AppTheme
import com.bikompanion.ui.theme.isDarkTheme
import com.bikompanion.ui.watch.StationWatchEvent
import kotlin.time.Duration.Companion.milliseconds
import kotlin.time.Duration.Companion.seconds

/** See the note where the orientation provider is built. */
private val HeadingSampleInterval = 50.milliseconds

@Composable
fun HomeScreen(
    locationProvider: LocationProvider = rememberDefaultLocationProvider(),
    showGlassPlayground: Boolean = false,
    showDebugNotificationButton: Boolean = false,
    onDebugNotificationClick: () -> Unit = {},
    requestWatchPermission: ((Boolean) -> Unit) -> Unit = { it(true) },
    onStationWatchEvent: (StationWatchEvent) -> Unit = {},
    onDarkThemeChange: (Boolean) -> Unit = {},
    foldBounds: FoldBounds? = null,
    onShareLink: ((title: String, url: String) -> Boolean)? = null,
    shareBaseUrl: String = StationLinks.ProductionBaseUrl,
    settingsPlatform: SettingsPlatform = SettingsPlatform(),
    appInstallOffer: AppInstallOffer? = null,
    viewModel: HomeViewModel = metroViewModel(),
) {
    // Without a heading provider `locationState.lastHeading` is always null and the only
    // bearing available is the course, which a rider standing still does not have — so the
    // heading-follow below would have nothing to turn the map by until they moved off.
    // Android and iOS read their compass here; desktop and web have none and get null, which
    // degrades to course-only tracking rather than failing.
    // The interval is what the sensor is sampled down to, and its default of one second is a
    // heading that arrives in visible steps — the map jumped once a second rather than turning.
    // Twenty a second is well under what the rotation-vector sensor delivers anyway, and the
    // spring below carries the frames in between.
    val locationState = rememberLocationState(
        provider = locationProvider,
        headingProvider = rememberDefaultHeadingProvider(),
        headingRequest = HeadingRequest(from = HeadingRequest.Standard) { minimumInterval = HeadingSampleInterval },
    )
    var locateMode by remember { mutableStateOf(LocateMode.Off) }
    // Set once a station link, notification or tap has flown the camera to a station, so the
    // first-fix auto-centre below doesn't fly it away again.
    var cameraFocusedOnStation by remember { mutableStateOf(false) }
    val heading = rememberSmoothedHeading(locationState)
    val homeState by viewModel.stateFlow.collectAsStateWithLifecycle()
    val darkTheme = homeState.themeMode.isDarkTheme()
    val currentOnDarkThemeChange by rememberUpdatedState(onDarkThemeChange)
    // Hosts draw chrome outside this composition (Android's system bar icons), so they need
    // the resolved value rather than the system setting.
    LaunchedEffect(darkTheme) { currentOnDarkThemeChange(darkTheme) }
    val cameraState = rememberMapState(
        baseStyle = buildMapStyle(darkTheme),
        initialCameraPosition = CameraPosition(
            center = homeState.startPosition?.toPosition() ?: FallbackStart,
            zoom = StartZoom,
        ),
        content = {
            UserLocationPuck(
                location = locationState.lastLocation,
                heading = { heading.value },
                showCone = locateMode == LocateMode.FollowingHeading,
            )
        },
    )
    val coroutineScope = rememberCoroutineScope()
    val currentOnStationWatchEvent by rememberUpdatedState(onStationWatchEvent)

    val pendingWatchEvents = homeState.pendingStationWatchEvents
    LaunchedEffect(pendingWatchEvents) {
        if (pendingWatchEvents.isEmpty()) return@LaunchedEffect
        pendingWatchEvents.forEach { currentOnStationWatchEvent(it) }
        viewModel.onAction(HomeScreenActions.StationWatchEventsConsumed(pendingWatchEvents.size))
    }

    VisibleNetworksEffect(
        cameraState = cameraState,
        networks = { homeState.networks },
        onVisibleNetworksChange = { networkIds, zoom, visibleBounds ->
            viewModel.onAction(HomeScreenActions.VisibleNetworksChanged(networkIds, zoom, visibleBounds))
        },
    )

    // Availability polling costs data, so it runs only while the screen is actually on
    // screen — a composition alone outlives a backgrounded app. STARTED rather than RESUMED
    // to match the location stream the map already runs, and because a window that is merely
    // unfocused (split screen, a dialog over it) is still being looked at.
    LifecycleStartEffect(viewModel) {
        viewModel.onAction(HomeScreenActions.AutoRefreshStarted)
        onStopOrDispose { viewModel.onAction(HomeScreenActions.AutoRefreshStopped) }
    }

    // Derived rather than keyed on the map's size: a refresh replaces the list under an
    // existing key, which leaves the size untouched, and the markers would keep drawing the
    // counts they were first given. The equality policy is what keeps that cheap — a poll
    // that comes back unchanged compares equal and invalidates nothing downstream.
    val stations by remember(viewModel) {
        derivedStateOf(structuralEqualityPolicy()) {
            homeState.stationsByNetwork.values.flatten()
        }
    }

    // Flattened the same way and for the same reason: a cell that comes back identical
    // compares equal and invalidates nothing downstream.
    val parkingSpots by remember(viewModel) {
        derivedStateOf(structuralEqualityPolicy()) {
            homeState.parkingByCell.values.flatten()
        }
    }

    // Fed to the map's own camera and to the cone drawn on the overlay, so both turn as one.
    FollowCameraEffect(
        locateMode = locateMode,
        locationState = locationState,
        heading = { heading.value },
        cameraState = cameraState,
    )

    StopFollowingOnGestureEffect(
        locateMode = locateMode,
        cameraState = cameraState,
        onGesture = { locateMode = LocateMode.Off },
    )

    AppTheme(darkTheme = darkTheme, languageTag = homeState.language.tag) {
        HomeNavHost(
            backStack = homeState.backStack,
            onBack = { viewModel.onAction(HomeScreenActions.StationDismissed) },
        ) {
            HomeContent(
                darkTheme = darkTheme,
                themeMode = homeState.themeMode,
                onThemeModeSelect = { viewModel.onAction(HomeScreenActions.ThemeModeSelected(it)) },
                language = homeState.language,
                onLanguageSelect = { viewModel.onAction(HomeScreenActions.LanguageSelected(it)) },
                settingsPage = homeState.settingsPage,
                settingsPlatform = settingsPlatform,
                onSettingsPageOpen = { viewModel.onAction(HomeScreenActions.SettingsPageOpened(it)) },
                onSettingsBack = { viewModel.onAction(HomeScreenActions.SettingsBack) },
                onSettingsClose = { viewModel.onAction(HomeScreenActions.SettingsClosed) },
                networks = homeState.networks,
                stations = stations,
                parkingSpots = parkingSpots,
                parkingAreas = homeState.parkingAreas,
                isLoadingStations = homeState.isLoadingStations,
                noNetworkInView = homeState.noNetworkInView,
                isLoadingParking = homeState.isLoadingParking,
                parkingLoadFailed = homeState.parkingLoadFailed,
                onRetryLoadParkingClick = { viewModel.onAction(HomeScreenActions.ParkingRetryRequested) },
                changedAt = homeState.changedAt,
                cameraState = cameraState,
                locationState = locationState,
                networksLoadFailed = homeState.networksLoadFailed,
                refreshStatus = homeState.refreshStatus,
                freshness = homeState.freshness,
                parkingCountdownStatus = homeState.parkingCountdownStatus,
                mapLayerMode = homeState.mapLayerMode,
                onMapLayerModeSelect = { viewModel.onAction(HomeScreenActions.MapLayerModeSelected(it)) },
                ratingUiState = homeState.ratingUiState,
                onRatingBikeNumberChange = { viewModel.onAction(HomeScreenActions.RatingBikeNumberChanged(it)) },
                onRatingElectricToggle = { viewModel.onAction(HomeScreenActions.RatingElectricToggled(it)) },
                onVoteSelect = { viewModel.onAction(HomeScreenActions.VoteSelected(it)) },
                onIssueToggle = { viewModel.onAction(HomeScreenActions.IssueToggled(it)) },
                onRatingNetworkSelect = { viewModel.onAction(HomeScreenActions.RatingNetworkSelected(it)) },
                onRatingPositionChange = { latitude, longitude, source ->
                    viewModel.onAction(HomeScreenActions.RatingPositionChanged(latitude, longitude, source))
                },
                foldBounds = foldBounds,
                onSubmitVoteClick = { viewModel.onAction(HomeScreenActions.SubmitClicked) },
                onRatingDialogDismiss = { viewModel.onAction(HomeScreenActions.RatingDialogDismissed) },
                watches = homeState.stationWatches,
                onWatch = { station, bikeType ->
                    requestWatchPermission { granted ->
                        if (granted) {
                            viewModel.onAction(HomeScreenActions.StationWatchRequested(station, bikeType))
                        }
                    }
                },
                onCancelWatch = { station, bikeType ->
                    viewModel.onAction(HomeScreenActions.StationWatchCancelled(station, bikeType))
                },
                stationFocusTarget = homeState.stationFocusTarget,
                onStationFocusHandle = { target ->
                    locateMode = LocateMode.Off
                    cameraFocusedOnStation = true
                    viewModel.onAction(HomeScreenActions.StationFocusHandled(target))
                },
                locateMode = locateMode,
                onCompassClick = {
                    // The compass turns the map back to north, which is the opposite of what
                    // heading-follow is doing; leaving it running would spin the map straight back.
                    // Centring survives, because that is about where the camera is, not which way.
                    if (locateMode == LocateMode.FollowingHeading) locateMode = LocateMode.Centered
                },
                // Off → Centered → FollowingHeading → Off, one tap apart. The last step is a way
                // out of following that doesn't require panning the map somewhere else first.
                onLocateMeClick = {
                    when (locateMode) {
                        LocateMode.Off -> {
                            coroutineScope.launch {
                                // Entered here rather than on the tap: the mode is what the FAB icon
                                // draws, and until there is a fix the camera is not on the rider.
                                if (flyToRider(locationState, cameraState)) locateMode = LocateMode.Centered
                            }
                        }

                        // Already on the rider, so nothing has to be waited for — the tracking
                        // effect turns the map on the next reading the compass gives it.
                        LocateMode.Centered -> {
                            locateMode = LocateMode.FollowingHeading
                        }

                        LocateMode.FollowingHeading -> {
                            locateMode = LocateMode.Off
                            coroutineScope.launch {
                                // Left mid-turn, the map keeps the bearing the last fix gave it and
                                // every label on it stays crooked — with the compass, which fades
                                // out along with the mode, no longer there to say why.
                                cameraState.animateCamera(CameraUpdate(bearing = 0.0, pitch = 0.0))
                            }
                        }
                    }
                },
                showGlassPlayground = showGlassPlayground,
                showDebugNotificationButton = showDebugNotificationButton,
                onDebugNotificationClick = onDebugNotificationClick,
                selectedStationTarget = homeState.selectedStationTarget,
                onStationSelect = { viewModel.onAction(HomeScreenActions.StationSelected(it)) },
                onStationDismiss = { viewModel.onAction(HomeScreenActions.StationDismissed) },
                shareBaseUrl = shareBaseUrl,
                onShareLink = onShareLink,
                appInstallOffer = appInstallOffer,
                appInstallDismissed = homeState.installPromptDismissed,
                onAppInstallDismiss = { viewModel.onAction(HomeScreenActions.InstallPromptDismissed) },
            )
        }
    }

    StartPositionEffect(
        startPosition = homeState.startPosition,
        cameraState = cameraState,
        cameraOwnedElsewhere = { cameraFocusedOnStation || homeState.stationFocusTarget != null },
    )

    // Auto-center on user location when the map opens if we have a fix — unless a station already
    // owns the camera. A first fix takes a few seconds, so with a station link it lands after the
    // flight to the station and would otherwise pull the map back to the rider. A link still
    // waiting for its station to load counts too: the flight is coming, so this one is skipped
    // rather than made first.
    LaunchedEffect(Unit) {
        val location = snapshotFlow { locationState.lastLocation }.filterNotNull().first()
        if (cameraFocusedOnStation || homeState.stationFocusTarget != null) return@LaunchedEffect
        cameraState.animateCamera(
            CameraUpdate(center = location.position, zoom = 16.0),
            CameraAnimation.Fly.Standard,
        )
    }
}
